/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-continue */
import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useParams } from 'react-router-dom';
// @mui
import { Card, Container, Grid, TextField, IconButton, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';

// components
import useAuth from '../hooks/useAuth';
import { getCachedDataCapture, invalidateDataCaptureCache, getStreamingLink, updateDataCapture } from '../services/dataCaptureService';
import { requestTranscription, retriveTranscription } from '../services/transcriptionService';
import { createRule, getAllRules } from '../services/ruleService';
import MapViewer from '../components/dataviewers/MapViewer';
import LoadingSpinner from '../components/LoadingSpinner';

import {
  SessionPageChartTabbedDisplay,
  SessionPageHeader,
  SessionPageVideoPlayer,
  SessionDetailsCard,
  SessionEventTimeLine,
} from '../sections/@dashboard/session';
import { StandardCreationDialog } from '../sections/@dashboard/standards';
import SessionExportDialog from '../sections/@dashboard/session/SessionExportDialog';
import { getSensorType } from '../services/localizationService';
import ShareDialog from '../components/share/ShareDialog';
import Iconify from '../components/iconify';
import { prefersFahrenheit } from '../utils/temperature';

const MAX_GRAPH_COUNT = 25;

// ----------------------------------------------------------------------
export default function DataCapturePage() {
  const { dataCaptureId } = useParams();
  const videoRef = useRef(null);
  const { user } = useAuth();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [dataCapture, setDataCapture] = useState([]);

  const [loading, setLoading] = useState(true);
  const [graphCount, setGraphCount] = useState(1);
  const [sensorTypes, setSensorTypes] = useState([]);
  const [stepperActiveStep, setStepperActiveStep] = useState(0);
  const [standards, setStandards] = useState([
    { name: 'test', id: 1, description: 'test', type: 'greater', value: 0.5 },
  ]);
  const [sensorEvents, setSensorEvents] = useState([]);
  const [allSensorData, setAllSensorData] = useState({});
  const [mapCoordinates, setMapCoordinates] = useState({});
  const [sensorsByType, setSensorsByType] = useState({});
  const [activeStandards, setActiveStandards] = useState({});
  const [lagTime, setLagTime] = useState(5000);
  const [initalStartAndEndEvents, setInitalStartAndEndEvents] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [createStandardModalShown, setCreateStandardModalShown] = useState(false);
  const [exportModalShown, setExportModalShown] = useState(false);
  const [videoStreamingLink, setVideoStreamingLink] = useState('');
  const [transcription, setTranscription] = useState('');
  const [eventSensor, setEventSensor] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [videoRotation, setVideoRotation] = useState(0);

  // Permissions
  const [effectivePermission, setEffectivePermission] = useState(null);
  const [isOwner, setIsOwner] = useState(false);

  // Edit Mode
  const [editMode, setEditMode] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // Editable Fields
  const [dataCaptureName, setDataCaptureName] = useState('');
  const [dataCaptureDescription, setDataCaptureDescription] = useState('');

  useHotkeys('space', (e) => {
    e.preventDefault();
    setIsPlaying(!isPlaying);
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const timestamp = urlParams.get('timestamp');
    const standardId = urlParams.get('standardId');
    if (!timestamp) return;

    if (videoRef.current && timestamp && standardId) {
      window.history.pushState({}, document.title, window.location.pathname);
      addSelectedStandard(standards.find((standard) => standard.id === standardId));
      handleTimelineClick({ startTime: Number(timestamp) });
    }
  }, [videoRef.current]);

  const createStartAndEndEvents = (startTime, endTime) => {
    const events = [
      {
        id: 'start-id',
        label: 'Start',
        startTime,
        endTime: startTime,
      },
      {
        id: 'end-id',
        label: 'End',
        startTime: endTime,
        endTime,
      },
    ];
    setInitalStartAndEndEvents(events);
  };

  const playSession = () => {
    if (new Date(dataCapture.endTime).getTime() <= currentTime.getTime()) {
      resetSession();
    }

    setIsPlaying(true);
  };

  const pauseSession = () => {
    setIsPlaying(false);
  };

  const resetSession = () => {
    setIsPlaying(false);
    const startDate = new Date(dataCapture.startTime);
    setCurrentTime(startDate);
    setVideoTime(startDate);
  };

  const addSelectedStandard = (standard) => {
    activeStandards[standard.id] = standard;
    setActiveStandards(activeStandards);
    updateEvents(activeStandards);
  };

  const removeActiveStandard = (standardId) => {
    // remove the standard from the existing active standards array
    delete activeStandards[standardId];
    setActiveStandards(activeStandards);
    updateEvents(activeStandards);
  };

  const convertSensorsToSensorByTypeMap = (sensors) => {
    const keys = Object.keys(sensors);
    const sensorTypes = [...new Set(keys.map((key) => getSensorType(key.split(':').pop())))];

    const sensorsByType = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const sensorType of sensorTypes) {
      const sensorDataArray = keys.filter((key) => getSensorType(key.split(':').pop()) === sensorType);
      sensorsByType[sensorType] = sensorDataArray;
    }
    setSensorsByType(sensorsByType);
  };

  const updateEvents = (activeStandards) => {
    const newEvents = createEventsFromStandards(activeStandards);
    sortEvents(newEvents);
    setSensorEvents(newEvents);
  };

  const sortEvents = (events) => {
    events.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    return events;
  };

  const createEventsFromStandards = (activeStandards) => {
    const keys = Object.keys(allSensorData);
    const sensorTypes = keys.map((key) => getSensorType(key.split(':').pop()));
    const eventsFromStandards = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const [k, standard] of Object.entries(activeStandards)) {
      if (sensorTypes.includes(getSensorType(standard.sensorType))) {
        const sensorDataArray = keys.filter(
          (key) => getSensorType(key.split(':').pop()) === getSensorType(standard.sensorType)
        );
        sensorDataArray.forEach((sensorDataKey) => {
          const sensorData = allSensorData[sensorDataKey];
          const newEvents = getEventsFromDataAndStandards(sensorData, standard);
          const consolidated = consolidateEvents(newEvents, lagTime);
          eventsFromStandards.push(...consolidated);
        });
      }
    }
    return eventsFromStandards;
  };

  function consolidateEvents(events, lagTime) {
    // Sort events by end time
    events.sort((a, b) => new Date(a.endTime) - new Date(b.endTime));

    // Initialize variables
    const consolidatedEvents = [];
    let currentEvent = null;

    // Loop through events
    for (let i = 0; i < events.length; i += 1) {
      const event = events[i];

      // If there is no current event, set it to the current event and continue to the next event
      if (!currentEvent) {
        currentEvent = event;
        continue;
      }

      // If the current event's end time is within the lag time of the next event's start time,
      // consolidate the events and continue to the next event

      let currentEventEndTime = new Date(currentEvent.endTime);
      currentEventEndTime = currentEventEndTime.setTime(currentEventEndTime.getTime() + lagTime);

      if (new Date(currentEventEndTime) >= new Date(event.startTime)) {
        currentEvent.endTime = event.endTime;
      } else {
        // Otherwise, add the current event to the consolidated events array
        consolidatedEvents.push(currentEvent);
        currentEvent = event;
      }
    }

    // Add the last event to the consolidated events array
    if (currentEvent) {
      consolidatedEvents.push(currentEvent);
    }

    return consolidatedEvents;
  }

  const getEventsFromDataAndStandards = (sensorData, standard) => {
    const newEvents = [];
    const standardType = standard.operator;
    const standardValue = parseFloat(standard.value);
    const label = `Event: ${standard.name}`;
    let i = 0;
    while (i < sensorData.length) {
      const dataPoint = sensorData[i];
      const value = parseFloat(dataPoint.value);
      if (standardType === 'greater') {
        if (value > standardValue) {
          const event = {
            id: standard.id,
            label,
            startTime: dataPoint.time,
            endTime: dataPoint.time,
            value,
          };
          newEvents.push(event);
        }
      }
      if (standardType === 'lesser') {
        if (value < standardValue) {
          const event = {
            id: standard.id,
            label,
            startTime: dataPoint.time,
            endTime: dataPoint.time,
            value,
          };
          newEvents.push(event);
        }
      }
      i += 1;
    }
    return newEvents;
  };

  function setVideoTime(time) {
    if (!videoRef.current) return;
    if (
      determineDurationInVideo(time, dataCapture.startTime) > 0 &&
      determineDurationInVideo(time, dataCapture.endTime) < 0
    ) {
      videoRef.current.seekTo(determineDurationInVideo(time, dataCapture.startTime) / 1000);
    } else {
      videoRef.current.seekTo(0);
    }
  }

  function determineDurationInVideo(targetTime, ct) {
    const duration = new Date(targetTime) - new Date(ct);
    return duration;
  }

  const processAndSetDataCapture = (res) => {
    if (prefersFahrenheit(user)) {
      const key = Object.keys(res.data).find(
        (el) => el.split(':').pop() === 'Temp' || el.split(':').pop() === 'TEMPERATURE_DATA'
      );

      if (res.data[key]) {
        res.data['null:TEMP_F'] = res.data[key].map((obj) => ({
          ...obj,
          value: ((obj.value * 9) / 5 + 32).toFixed(2),
        }));
        delete res.data[key];
      }
    }
    setDataCapture(res);
    setVideoRotation(res.videoRotation || 0);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      invalidateDataCaptureCache(dataCaptureId);
      const res = await getCachedDataCapture(dataCaptureId);
      processAndSetDataCapture(res);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      const res = await getCachedDataCapture(dataCaptureId);
      processAndSetDataCapture(res);
      if (!res.sensorsOnly) {
        getStreamingLink(dataCaptureId).then((link) => {
          setVideoStreamingLink(link);
        });
      }
    }
    fetchData();
  }, [dataCaptureId]);

  useEffect(() => {
    if (dataCapture.length === 0) {
      return;
    }

    if (dataCapture.data) {
      setAllSensorData(dataCapture.data);
    }
    setCurrentTime(new Date(dataCapture.startTime));

    getAllRules().then((res) => {
      setStandards(res);
    });
    setAllSensorData(dataCapture.data);
    const keys = Object.keys(dataCapture.data);
    const sensorTypes = keys.map((key) => key.split(':').pop());
    convertSensorsToSensorByTypeMap(dataCapture.data);
    setSensorTypes([...new Set(sensorTypes)]);

    if (keys.includes('builtinGPS:longitude')) {
      const longitude = dataCapture.data['builtinGPS:longitude'];
      const latitude = dataCapture.data['builtinGPS:latitude'];
      const mapCoordinates = longitude.map((obj, index) => ({
        lat: Number(latitude[index].value),
        lng: Number(longitude[index].value),
        timestamp: Number(obj.time),
      }));
      setMapCoordinates(mapCoordinates);
    }
    if (dataCapture.data) {
      createStartAndEndEvents(dataCapture.startTime, dataCapture.endTime);
      setCurrentTime(new Date(dataCapture.startTime));
      setLoading(false);
      createEventsFromStandards(activeStandards);
    }
  }, [dataCapture]);

  useEffect(() => {
    if (dataCapture && user) {
      const owner = dataCapture.ownerId === user.id;
      setIsOwner(owner);
      setEffectivePermission(dataCapture.effectivePermission || (owner ? 'full_access' : null));
    }
  }, [dataCapture, user]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        let newTime = new Date(currentTime.getTime() + 1000);
        if (sensorEvents.length > 0 && !withinAnEvent(newTime)) {
          // find next event
          const rampUpTime = currentTime.getTime() + lagTime + 1000;
          const nextEvent = sensorEvents.find((event) => new Date(event.startTime) > rampUpTime);

          if (nextEvent) {
            setStepperActiveStep(sensorEvents.indexOf(nextEvent) + 1);
            const time = new Date(nextEvent.startTime).getTime() - lagTime;

            setVideoTime(time);
            newTime = new Date(time);
          }
        }

        setCurrentTime(newTime);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, currentTime, sensorEvents, withinAnEvent, lagTime, setVideoTime]);

  useEffect(() => {
    if (currentTime) {
      if (currentTime >= new Date(dataCapture.endTime)) {
        pauseSession();
      }
    }
  }, [currentTime, dataCapture.endTime]);

  function withinAnEvent(time) {
    const newEvents = sensorEvents.filter(
      (event) =>
        new Date(event.startTime - lagTime) <= time && new Date(new Date(event.endTime).getTime() + lagTime) >= time
    );
    if (newEvents.length > 0) {
      return true;
    }
    return false;
  }

  const createStandard = () => {
    setCreateStandardModalShown(true);
  };

  const addNewStandard = async (newStandard) => {
    createRule(newStandard).then((res) => {
      setStandards([...standards, res]);
    });
  };

  const addTemporaryStandard = ({ description, id, name, operator, sensorType, value, isTemp }) => {
    const standard = {
      description,
      id,
      name,
      operator,
      sensorType,
      value: String(value),
      isTemp,
    };
    setStandards([...standards, standard]);
    addSelectedStandard(standard);
  };

  const closeCreateStandardDialog = () => {
    setCreateStandardModalShown(false);
  };

  const handleTimelineClick = (event) => {
    let time = new Date(new Date(event.startTime).getTime() - lagTime);
    if (time < new Date(dataCapture.startTime)) {
      time = new Date(dataCapture.startTime);
    }
    setVideoTime(time);
    setCurrentTime(new Date(time));

    if (event.sensor) {
      setEventSensor(event.sensor);
    }
  };

  const updateGraphCount = (count) => {
    if (count < 0) {
      count = 0;
    }
    if (count > MAX_GRAPH_COUNT) {
      count = MAX_GRAPH_COUNT;
    }
    setGraphCount(count);
  };

  if (loading) {
    return <LoadingSpinner message="Loading data capture..." />;
  }

  const onVideoReady = () => {
    // Video player is ready
  };

  const onTranscriptionLoad = () => {
    retriveTranscription(dataCaptureId).then((res) => {
      setTranscription(res);
    });
  };

  const handleEditMode = () => {
    if (editMode) {
      saveNewTitleAndDescription(dataCapture);
      setEditMode(false);
    } else {
      setDataCaptureName(dataCapture.name);
      setDataCaptureDescription(dataCapture.description);
      setEditMode(true);
    }
  };

  const handleCancelEditMode = () => {
    setEditMode(false);
    setDataCaptureName(dataCapture.name);
    setDataCaptureDescription(dataCapture.description);
  };

  const saveNewTitleAndDescription = (dataCapture) => {
    dataCapture.name = dataCaptureName;
    dataCapture.description = dataCaptureDescription;
    updateDataCapture(dataCapture);
  };

  const handleRotateVideo = () => {
    const newRotation = (videoRotation + 90) % 360;
    setVideoRotation(newRotation);
    dataCapture.videoRotation = newRotation;
    updateDataCapture(dataCapture);
  };

  return (
    <>
      <Helmet>
        <title> VPSI | {dataCapture.name} </title>
      </Helmet>
      <Container>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={12}>
            <SessionPageHeader
              lagTime={lagTime}
              lagTimeChanged={setLagTime}
              session={dataCapture}
              playing={isPlaying}
              playClicked={playSession}
              pauseClicked={pauseSession}
              resetClicked={resetSession}
              editMode={editMode}
              setName={(name) => {
                setDataCaptureName(name);
              }}
            />
          </Grid>
          {!dataCapture.sensorsOnly ? (
            <Grid item xs={12} md={12} lg={6}>
              <SessionPageVideoPlayer
                session={dataCapture}
                isPlaying={isPlaying}
                videoRef={videoRef}
                videoFileLocation={videoStreamingLink}
                onVideoReady={onVideoReady}
                playClicked={playSession}
                pauseClicked={pauseSession}
                resetClicked={resetSession}
                lagTime={lagTime}
                lagTimeChanged={setLagTime}
                videoRotation={videoRotation}
                onRotate={handleRotateVideo}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Card sx={{ px: 3, py: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      color="primary"
                      onClick={isPlaying ? pauseSession : playSession}
                      size="large"
                    >
                      {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    <IconButton color="primary" onClick={resetSession} size="large">
                      <ReplayIcon />
                    </IconButton>
                  </Stack>
                  <TextField
                    label="Lag Time"
                    type="number"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={Number.isNaN(lagTime) ? '' : lagTime / 1000}
                    onChange={(e) => setLagTime(parseInt(e.target.value, 10) * 1000)}
                    sx={{ width: 120 }}
                  />
                </Stack>
              </Card>
            </Grid>
          )}
          <Grid item xs={12} md={12} lg={dataCapture.sensorsOnly ? 12 : 6}>
            {user?.organizationId && (isOwner || effectivePermission === 'full_access') && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <IconButton color="primary" onClick={() => setShareDialogOpen(true)}>
                  <Iconify icon="eva:share-fill" />
                </IconButton>
              </Stack>
            )}
            <SessionDetailsCard
              session={dataCapture}
              currentTime={currentTime}
              standards={standards}
              addSelectedStandard={addSelectedStandard}
              activeStandards={activeStandards}
              removeActiveStandard={removeActiveStandard}
              createStandard={createStandard}
              openExport={() => setExportModalShown(true)}
              editMode={editMode}
              setEditMode={handleEditMode}
              cancelEditMode={handleCancelEditMode}
              setDescription={(description) => {
                setDataCaptureDescription(description);
              }}
              canEdit={isOwner || effectivePermission === 'editor' || effectivePermission === 'full_access'}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <SessionEventTimeLine
              setActiveStep={setStepperActiveStep}
              stepperActiveStep={stepperActiveStep}
              activeStandards={activeStandards}
              session={dataCapture}
              onEventClick={handleTimelineClick}
              events={sortEvents([...initalStartAndEndEvents, ...sensorEvents])}
              timeObject={{
                start: new Date(initalStartAndEndEvents[0].startTime),
                end: new Date(initalStartAndEndEvents[1].endTime),
                current: currentTime,
              }}
            />
          </Grid>
          {Array(graphCount)
            .fill(null)
            .map((_, index) => (
              <Grid key={index} item xs={12} md={12} lg={12}>
                <SessionPageChartTabbedDisplay
                  key={index}
                  session={dataCapture}
                  sensorByTypes={sensorsByType}
                  events={sensorEvents}
                  currentTime={currentTime}
                  onChartClick={handleTimelineClick}
                  eventSensor={index === 0 ? eventSensor : null}
                  setSensor={setEventSensor}
                  openExport={
                    (isOwner || effectivePermission === 'editor' || effectivePermission === 'full_access')
                      ? () => setExportModalShown(true)
                      : undefined
                  }
                  onRefresh={handleRefresh}
                  refreshing={refreshing}
                />
              </Grid>
            ))}
          <Grid item xs={12} md={12} lg={12}>
            {mapCoordinates.length > 0 && (
              <MapViewer coordinates={mapCoordinates} currentTime={currentTime} events={sensorEvents} />
            )}
          </Grid>
        </Grid>
      </Container>
      <StandardCreationDialog
        open={createStandardModalShown}
        handleClose={closeCreateStandardDialog}
        handleSave={addNewStandard}
        sensorTypes={sensorTypes}
        addTemporaryStandard={addTemporaryStandard}
      />
      <SessionExportDialog
        open={exportModalShown}
        handleClose={() => setExportModalShown(false)}
        handleSave={addNewStandard}
        sensorTypes={sensorTypes}
        data={dataCapture.data}
        captureName={dataCapture.name}
      />
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        dataCaptureId={dataCapture?.id}
        ownerId={dataCapture?.ownerId}
        userOrganizationId={user?.organizationId}
      />
    </>
  );
}
